import React from 'react';
import { Helmet } from 'react-helmet-async';

const BASE_URL = 'https://www.needahairmakeoverfranchise.co.uk';

// Default SEO values
const defaultSEO = {
  title: 'Hair Extensions Franchise Opportunity | Need A Hair Makeover',
  description: 'Join Need A Hair Makeover\'s award-winning specialist salon franchise. Expert training in 18 hair extension techniques, hair loss treatments, and bespoke solutions. Franchise opportunities across the UK.',
  image: 'https://customer-assets.emergentagent.com/job_hair-opportunity/artifacts/v5y5ircx_YES%20I%20WANT%20TO%20ENROL_20250913_202151_0002.png',
};

// Page-specific SEO configurations
export const pageSEO = {
  home: {
    title: 'Hair Extensions Franchise Opportunity | Need A Hair Makeover',
    description: 'Join Need A Hair Makeover\'s award-winning specialist salon franchise. Expert training in 18 hair extension techniques, hair loss treatments, and bespoke solutions. Franchise opportunities across the UK.',
    path: '/',
  },
  franchiseOpportunity: {
    title: 'Start a Hair Extensions & Hair Loss Franchise | Need A Hair Makeover',
    description: 'Launch your own specialist hair salon franchise. High-demand, recession-resistant business with full training in 18+ techniques. Investment starts at £68,000. No beauty experience required.',
    path: '/hair-extensions-franchise-opportunity',
  },
  press: {
    title: 'Blog, Press & Media | Need A Hair Makeover Franchise',
    description: 'Latest blog posts, media resources, press releases, and downloadable assets for journalists covering Need A Hair Makeover - the UK\'s award-winning hair extensions and hair loss specialist franchise.',
    path: '/press',
  },
  resources: {
    title: 'Resources & Industry Insights | Need A Hair Makeover Franchise',
    description: 'Industry research, franchise guides, and insights into the hair extensions and hair loss specialist market. Learn why this is one of the fastest-growing beauty sectors.',
    path: '/resources',
  },
  sponsorship: {
    title: 'Sponsorship Opportunities | Need A Hair Makeover Franchise',
    description: 'Partner with Need A Hair Makeover - explore sponsorship opportunities with the UK\'s leading hair extensions and hair loss specialist franchise.',
    path: '/sponsorship-opportunity',
  },
  privacy: {
    title: 'Privacy Policy | Need A Hair Makeover Franchise',
    description: 'Privacy policy for Need A Hair Makeover Franchise website. Learn how we collect, use, and protect your personal information.',
    path: '/privacy',
  },
  thankYou: {
    title: 'Thank You | Need A Hair Makeover Franchise',
    description: 'Thank you for your interest in Need A Hair Makeover franchise. We will be in touch soon.',
    path: '/thank-you',
  },
  trainingAcademy: {
    title: 'Hair Extensions Training Academy | Need A Hair Makeover',
    description: 'Professional hair extensions and hair loss specialist training courses. Learn 18+ techniques from industry experts with 25+ years experience. Accredited certification included.',
    path: '/training-academy',
  },
  apply: {
    title: 'Apply Now | Need A Hair Makeover Franchise',
    description: 'Apply for a Need A Hair Makeover franchise opportunity. Get your free franchise brochure or submit your application to join the UK\'s leading hair extensions and hair loss specialist franchise.',
    path: '/apply',
  },
};

/**
 * SEO Component - provides page-specific meta tags
 * @param {Object} props
 * @param {string} props.page - Page key from pageSEO object
 * @param {string} props.title - Custom title (overrides pageSEO)
 * @param {string} props.description - Custom description (overrides pageSEO)
 * @param {string} props.image - Custom OG image URL
 * @param {Object} props.schema - Additional JSON-LD schema
 */
export const SEO = ({ 
  page, 
  title: customTitle, 
  description: customDescription, 
  image: customImage,
  schema 
}) => {
  const pageConfig = pageSEO[page] || {};
  
  const title = customTitle || pageConfig.title || defaultSEO.title;
  const description = customDescription || pageConfig.description || defaultSEO.description;
  const image = customImage || defaultSEO.image;
  const path = pageConfig.path || '/';
  const url = `${BASE_URL}${path}`;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      
      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={url} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      
      {/* Additional Schema if provided */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

export default SEO;
